export interface TerminologyCacheItemConfig {
  /**
   * FHIR resource path relative to the FHIR base URL, e.g.:
   * - 'ValueSet/nafdac-medications'
   * - 'CodeSystem/service-request-codes'
   * - 'ValueSet/*' (wildcard for all single-resource ValueSet reads)
   * - 'CodeSystem/*' (wildcard for all single-resource CodeSystem reads)
   */
  path: string;

  /**
   * Bump this version string (e.g. 'v1' -> 'v2') whenever you update the ValueSet/CodeSystem
   * data on the server and want every browser/device to immediately discard its cached copy.
   */
  version?: string;

  /**
   * Custom Time-To-Live (in milliseconds) in the browser's persistent Cache Storage (window.caches)
   * before a silent background refresh (stale-while-revalidate) is triggered.
   * Defaults to TERMINOLOGY_CACHE_CONFIG.defaultTtlMs (24 hours).
   */
  ttlMs?: number;

  /**
   * If true, this resource is silently prefetched in the background after app startup
   * (with LoadingUIEnabled = false) if not already in the browser cache.
   */
  prefetchOnStartup?: boolean;
}

export const TERMINOLOGY_CACHE_CONFIG = {
  /**
   * Browser Cache Storage bucket name.
   * Changing `cacheVersion` invalidates ALL persistently cached terminology resources across all devices.
   */
  storagePrefix: 'elikita-fhir-terminology-cache',
  cacheVersion: 'v1',

  /**
   * Default Time-To-Live (24 hours) before an entry in window.caches is considered stale
   * and silently refreshed in the background (stale-while-revalidate).
   */
  defaultTtlMs: 24 * 60 * 60 * 1000,

  /**
   * Delay (in ms) after app startup before triggering silent background prefetch
   * for items with `prefetchOnStartup: true`.
   */
  prefetchDelayMs: 2500,

  /**
   * Query parameters automatically stripped when computing canonical cache keys for
   * configured terminology resources (so `/ValueSet/nafdac-medications` and
   * `/ValueSet/nafdac-medications?_sort=-_lastUpdated&_count=20` always hit the same cache entry).
   */
  ignoredQueryParams: ['_sort', '_count'],

  /**
   * Configured ValueSets and CodeSystems for canonical key normalization + persistent browser caching.
   * Add any additional ValueSet or CodeSystem path here by simply adding another object to this array.
   */
  items: [
    {
      // National NAFDAC medication catalog
      path: 'ValueSet/nafdac-medications',
      version: 'v1',
      prefetchOnStartup: true,
    },
    // --- Space to add additional ValueSets or CodeSystems: ---
    // Example:
    // {
    //   path: 'ValueSet/prescription-categories',
    //   version: 'v1',
    //   prefetchOnStartup: false,
    // },
    // {
    //   path: 'CodeSystem/service-request-codes',
    //   version: 'v1',
    //   prefetchOnStartup: false,
    // },
  ] as TerminologyCacheItemConfig[],
};
